/**
 * Bundle 44a - Persistent admin footer strip.
 *
 * Shows env + bundle number + git sha at the bottom of every admin
 * page. If the environment detection reports a mismatch (e.g. prod
 * host serving with APP_ENV=development), the footer turns red and
 * exposes a banner across the admin shell.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import WhatsNewPopover from './WhatsNewPopover';

const fmtRel = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch (e) {
    return null;
  }
};

const AdminFooter = () => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/build-info')
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;

  const mismatch = info.env_mismatch;
  const envColor = mismatch
    ? 'bg-red-600 text-white'
    : info.env === 'production'
      ? 'bg-emerald-600 text-white'
      : info.env === 'preview'
        ? 'bg-blue-600 text-white'
        : 'bg-neutral-500 text-white';

  const highestBundle = (info.bundles_shipped || []).slice(-1)[0];
  const builtRel = fmtRel(info.built_at);

  return (
    <>
      {mismatch ? (
        <div
          data-testid="admin-env-mismatch-banner"
          className="bg-red-700 text-white px-4 py-2 text-sm text-center font-medium"
        >
          <AlertTriangle className="inline h-4 w-4 mr-2" />
          Environment mismatch. Serving on{' '}
          <code className="bg-red-900 px-1 rounded">{info.request_host}</code>
          {' '}but APP_ENV reports{' '}
          <code className="bg-red-900 px-1 rounded">{info.app_env || 'unset'}</code>
          . Contact ops.
        </div>
      ) : null}
      <footer
        data-testid="admin-footer"
        className="border-t border-neutral-200 bg-white px-6 py-2 text-xs text-neutral-700 flex flex-wrap items-center gap-x-4 gap-y-1"
      >
        <span
          data-testid="admin-footer-env"
          className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${envColor}`}
        >
          {info.env}
        </span>
        <span data-testid="admin-footer-bundle">
          <WhatsNewPopover highestBundle={highestBundle} />
        </span>
        <span>*</span>
        <span data-testid="admin-footer-git" title={info.git_sha_full}>
          git {info.git_sha_short}
        </span>
        {builtRel ? (
          <>
            <span>*</span>
            <span data-testid="admin-footer-built">Built {builtRel}</span>
          </>
        ) : null}
        <span>*</span>
        <Link
          to="/admin/health"
          className="underline hover:text-neutral-900"
          data-testid="admin-footer-health-link"
        >
          Data Health
        </Link>
      </footer>
    </>
  );
};

export default AdminFooter;
